import React, { FunctionComponent, useMemo } from "react";
import { AmpolService, fuelTypes } from "types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import IconAddress from "./icon-address";
import IconPhone from "./icon-phone";
import IconFuel from "./icon-fuel";
import IconAmenities from "./icon-amenities";

const Detail: FunctionComponent<DetailProps> = ({ onClose, ampolService }) => {
  const fuels = useMemo(() => {
    return (ampolService.services ?? [])
      .filter((service) => Object.keys(fuelTypes).includes(service))
      .map((service) => fuelTypes[service as keyof typeof fuelTypes]);
  }, [ampolService]);

  const services = useMemo(() => {
    const descriptions: string[] = [];
    if (ampolService.services?.includes("SHOWER")) {
      descriptions.push("Shower");
    }
    if (ampolService.services?.includes("FUELPAY")) {
      descriptions.push("FuelPay");
    }
    if (ampolService.services?.includes("CARWASH")) {
      descriptions.push("Carwash");
    }
    if (ampolService.services?.includes("ATM")) {
      descriptions.push("ATM");
    }
    if (ampolService.services?.includes("TOILET")) {
      descriptions.push("Toilet");
    }
    if (ampolService.services?.includes("BBQGAS")) {
      descriptions.push("BBQ Gas");
    }
    if (ampolService.services?.includes("TRUCKPARKING")) {
      descriptions.push("Truck Parking");
    }

    return descriptions;
  }, [ampolService]);

  const hasWOWDiscounts = useMemo(
    () => ampolService.services?.includes("WOWDiscounts"),
    [ampolService.services]
  );

  const googleDirections = useMemo(
    () =>
      `https://www.google.com/maps/dir/?api=1&destination=${ampolService.position[1]},${ampolService.position[0]}`,
    [ampolService]
  );

  return (
    <div className="tw-min-w-80 tw-h-full">
      <div className="tw-flex tw-flex-row tw-justify-end tw-pb-2">
        <button
          className="tw-border tw-rounded-full tw-p-1 tw-px-3 tw-w-max tw-bg-gray-600 tw-text-white tw-font-button"
          onClick={onClose}
        >
          Close <FontAwesomeIcon className="tw-inline" icon={faXmark} />
        </button>
      </div>
      <div className="tw-bg-white tw-rounded-xl">
        <div className="tw-flex tw-justify-center tw-bg-white tw-p-5 tw-rounded-t-xl">
          <img
            src="https://cdn.alpacamaps.com/nrma/ampol-member-benefits/ampol_logo_color_600w.png"
            className="tw-m-1 tw-p-2 tw-w-1/4"
            alt="Ampol Logo"
          />
        </div>
        <div className="tw-p-4">
          <h1 className="tw-text-xl tw-font-header tw-pb-2" data-testid="name">
            {ampolService.name}
          </h1>
          <dl className="tw-text-sm tw-details tw-pt-4">
            {/* <dt className="font-header address">Address</dt> */}
            <dd className="tw-pb-2 tw-flex tw-flex-row tw-place-items-center">
              <IconAddress className="tw-max-w-4 tw-mr-2 tw-aspect-square tw-text-black" />
              <div className="tw-flex-grow">
                <a className="hover:tw-underline" href={googleDirections}>
                  {ampolService.addressLineOne}, {ampolService.locality},{" "}
                  {ampolService.region} {ampolService.postalCode}
                </a>
              </div>
            </dd>
            {ampolService.phoneNumber != null && (
              <>
                {/* <dt className="font-header phone">Phone</dt> */}
                <dd className="tw-pb-2 tw-flex tw-flex-row tw-place-items-center">
                  <IconPhone className="tw-max-w-4 tw-mr-2 tw-aspect-square tw-text-black" />
                  <div className="tw-flex-grow">
                    <a href={`tel:${ampolService.phoneNumber}`}>
                      {ampolService.phoneNumber}
                    </a>
                  </div>
                </dd>
              </>
            )}
            {fuels != null && fuels.length > 0 && (
              <>
                {/* <dt className="font-header fuel">Fuel Types</dt> */}
                <dd className="tw-pb-2 tw-flex tw-flex-row tw-place-items-center">
                  <IconFuel className="tw-max-w-4 tw-mr-2 tw-aspect-square tw-text-black tw-mb-2" />
                  <div className="tw-flex-grow">{fuels.join(", ")}</div>
                </dd>
              </>
            )}
            {services.length > 0 && (
              <>
                {/* <dt className="font-header fuel">Amenities</dt> */}
                <dd className="tw-pb-2 tw-flex tw-flex-row tw-place-items-center">
                  <IconAmenities className="tw-max-w-4 tw-mr-2 tw-aspect-square tw-text-black" />
                  {services.join(", ")}
                </dd>
              </>
            )}
          </dl>
          {hasWOWDiscounts === false && (
            <img
              src="//cdn.alpacamaps.com/nrma/ampol-member-benefits/tag-my-nrma-discount.png"
              alt="My NRMA discount"
              width="150"
            />
          )}
          {hasWOWDiscounts === true && (
            <>
              <img
                src="//cdn.alpacamaps.com/nrma/ampol-member-benefits/tag-my-nrma-everyday-rewards-discount.png"
                alt="Everyday Rewards discount"
                width="200"
              />
            </>
          )}

          <div className="tw-pt-4 tw-flex tw-justify-center ctas">
            <a
              href={googleDirections}
              target="_blank"
              rel="noreferrer"
              title="Click to open in new window"
              className="tw-border tw-rounded-full tw-p-4 tw-px-6 tw-justify-center tw-flex tw-bg-nrma-dark-blue tw-text-white tw-font-button tw-w-40"
            >
              Get directions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export type DetailProps = {
  ampolService: AmpolService;
  onClose: VoidFunction;
};

export default React.memo(Detail);
